import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, User, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '../store/authStore';
import { BRAND } from '../lib/constants';

export default function Login() {
    const navigate = useNavigate();
    const { login, loading } = useAuth();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        const res = await login(username.trim(), password);
        if (res.ok) {
            navigate('/home');
        } else {
            setError(res.error);
        }
    };

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <header className="border-b border-[#E5E7EB]">
                <div className="page-container flex h-16 items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={BRAND.logoUrl} alt="ExcellenC" className="h-9" />
                        <div className="leading-none">
                            <div className="brand-wordmark text-[17px] leading-none">{BRAND.name}</div>
                            <div className="brand-wordmark-sub text-[11px] mt-0.5">{BRAND.subtitle}</div>
                        </div>
                    </Link>
                    <Link to="/" className="text-sm font-medium text-[#6B7280] hover:text-[#111827]">
                        Back to home
                    </Link>
                </div>
            </header>

            <div className="flex-1 grid lg:grid-cols-2 items-center">
                {/* Left visual */}
                <div className="hidden lg:flex flex-col justify-center h-full px-16 py-20 brand-hero-gradient text-white">
                    <div className="max-w-md">
                        <h2 className="text-4xl font-extrabold leading-tight">
                            Practice smart.<br />
                            Get placed faster.
                        </h2>
                        <p className="mt-4 text-white/85 text-base leading-relaxed">
                            PlacementPilot is your personal AI interview coach — built exclusively for
                            ExcellenC learners preparing for Data & Analytics roles.
                        </p>
                        <div className="mt-10 grid grid-cols-2 gap-4">
                            {[
                                { k: '5', v: 'Interview Rounds' },
                                { k: '15', v: 'Questions per Session' },
                                { k: '3', v: 'Career Tracks' },
                                { k: 'AI', v: 'Powered Feedback' },
                            ].map((s) => (
                                <div key={s.v} className="rounded-xl bg-white/10 backdrop-blur px-4 py-3">
                                    <div className="text-3xl font-extrabold">{s.k}</div>
                                    <div className="text-xs mt-1 text-white/80">{s.v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right form */}
                <div className="flex justify-center items-center py-12 px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="w-full max-w-md"
                    >
                        <div className="brand-card p-8">
                            <div className="mb-6">
                                <div className="inline-flex items-center gap-2 rounded-full bg-[#F1F5FD] px-3 py-1 text-xs font-semibold text-[#1C3A94]">
                                    <LogIn className="h-3.5 w-3.5" /> LOGIN
                                </div>
                                <h1 className="mt-3 text-2xl font-extrabold text-[#111827]">
                                    Welcome back
                                </h1>
                                <p className="mt-1 text-sm text-[#6B7280]">
                                    Sign in to continue your PlacementPilot journey.
                                </p>
                            </div>

                            {error && (
                                <div
                                    data-testid="login-error"
                                    className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                                >
                                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                                <div>
                                    <label className="mb-1.5 block text-xs font-semibold text-[#111827]">
                                        Username
                                    </label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
                                        <input
                                            data-testid="login-username"
                                            className="brand-input pl-9"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            placeholder="Enter your username"
                                            autoComplete="username"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="mb-1.5 block text-xs font-semibold text-[#111827]">
                                        Password
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
                                        <input
                                            data-testid="login-password"
                                            type="password"
                                            className="brand-input pl-9"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            autoComplete="current-password"
                                            required
                                        />
                                    </div>
                                </div>

                                <button
                                    data-testid="login-submit-btn"
                                    type="submit"
                                    disabled={loading}
                                    className="brand-btn-primary w-full"
                                >
                                    {loading ? 'Signing in…' : 'Sign In'}
                                </button>
                            </form>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}
